// Copyright (c) 2025 by Audere

import {
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { createStyleSheet } from "../utils/styles";
import { getErrorText } from "../utils/errors";

export const InfoAlert: React.FC<{ message: string }> = ({ message }) => {
  return (
    <Alert severity="info" role="alert">
      <AlertTitle>Info: </AlertTitle>
      {message}
    </Alert>
  );
};

export const WarningAlert: React.FC<{ message: string }> = ({ message }) => {
  return (
    <Alert severity="warning" role="alert">
      <AlertTitle>Warning: </AlertTitle>
      {message}
    </Alert>
  );
};

interface ErrorAlertProps {
  error: unknown;
  message?: string;
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({ error, message }) => {
  const errorMsg = getErrorText(
    error as FetchBaseQueryError | SerializedError | undefined
  );
  return (
    <Alert severity="error" role="alert">
      <AlertTitle>Error: {errorMsg}</AlertTitle>
      {message}
    </Alert>
  );
};

export const StatusInLine: React.FC<{ message: string }> = ({ message }) => {
  return (
    <Stack direction="row" spacing={1} sx={styles.statusStack}>
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  );
};

export type severity = "error" | "warning" | "info" | "success";

export interface SnackbarAlertProps {
  open: boolean;
  message: string;
  severity: severity;
  onClose: () => void;
}

export const SnackbarAlert: React.FC<SnackbarAlertProps> = ({
  open,
  message,
  severity = "success",
  onClose,
}) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert onClose={onClose} severity={severity} role="alert">
        {message}
      </Alert>
    </Snackbar>
  );
};

const styles = createStyleSheet({
  statusStack: {
    alignContent: "center",
    justifyContent: "center",
    padding: 1,
  },
});
